// Deterministic capture_items id derivation (RFC 4122 UUID v5) — SHARED.
//
// Moved from apps/frontend/src/capture/client/capture-item-id.ts so the
// BACKEND can derive the exact same ids (create-space template
// materialisation), closing the dual-authority id mint behind the
// "adding it to everything" fan-out (docs/plans/rootcause-echo-id-churn
// .DRAFT.md): the server used to materialise a created room's template
// rows at random v4 ids the client could not predict, so a tap in the
// sync-lag window derived a DIFFERENT (v5) id for a slot the server
// already held → 409 already_exists → re-home → a dead id left on the
// screen. With both authorities deriving from the slot's natural key,
// the tap-in-window create targets the SAME id and resolves via the
// idempotent-return path instead — no collision, no re-home, no
// id-mismatched echo. The frontend module re-exports from here so its
// import sites and the deterministic-id lint gate are unchanged.
//
// WHY deterministic ids at all (audit H11 / #659 — the re-home/quarantine
// root cause):
//   The createItem dispatch used to mint a fresh random UUID v4 per call.
//   Two creates for the SAME logical slot — a double-tap, a re-capture, an
//   offline retry — therefore produced DIFFERENT ids. The server's partial
//   unique index (capture_items_session_space_asset_unique /
//   _session_site_asset_unique, WHERE rejected_at IS NULL) then rejected
//   the second create with 409 already_exists at a DIFFERENT id, and the
//   connector RE-HOMED it onto the first — spinning up the entire re-home
//   registry + orphan quarantine apparatus (C2/C3/H5/H6/H7/H8/H17).
//   Deriving the id from the slot's NATURAL KEY makes the second create
//   target the SAME id; the server resolves it via `tryIdempotentReturn`
//   (create-item-client-id.ts) — a 200, NOT a 409. First-write-wins, by
//   construction.
//
// THE NATURAL KEY mirrors the two server partial-unique-index scopes
// exactly (schema-capture-items.ts):
//   - building/space capture → (capture_session_id, space_id, asset_type_id)
//   - site-scoped capture     → (capture_session_id, site_id,  asset_type_id)
//   `space_id` is non-null for the first; null for the second (site rows),
//   so it selects the branch. Both are present wherever an id is needed
//   (client INSERT and server template materialisation alike), so the id
//   is computable with no extra round-trip. site_id is constant per
//   session, so including it on the site branch cannot diverge two creates
//   of one slot nor collide two distinct slots.
//
// RUNTIME COMPATIBILITY: uses Web Crypto `crypto.subtle.digest` — available
// in the PWA (secure context), Node ≥ 18 (Encore backend, vitest), and the
// @powersync/node test runtime. Typed STRUCTURALLY below so this module
// compiles in the shared package without the DOM lib.

/**
 * Frozen UUID v5 namespace for capture_items ids.
 *
 * Provenance: minted once via crypto.randomUUID() on 2026-06-25 for the
 * H11 fix and FROZEN here permanently. It MUST NEVER change — altering it
 * would shift every derived id, re-introducing the very slot-collision
 * (two ids for one slot) this module exists to eliminate. It is an
 * arbitrary, dedicated namespace (not an RFC predefined one) so it cannot
 * collide with any other UUID v5 use.
 */
export const CAPTURE_ITEM_ID_NAMESPACE = '436fb69d-2bae-4963-9455-c51e0dec7f02'

export interface CaptureItemIdInput {
  /** capture_sessions.id this item belongs to. */
  readonly captureSessionId: string
  /** spaces.id for a building/space capture; null for a site-scoped row. */
  readonly spaceId: string | null
  /** sites.id (denormalised on the session); the site-branch discriminator. */
  readonly siteId: string
  /** asset_types.id being captured. */
  readonly assetTypeId: string
}

/**
 * Derive the deterministic capture_items.id for a slot. Same natural key
 * ⇒ same id (idempotent re-capture); any difference in session / space /
 * site / asset ⇒ a different id (distinct slots never collide).
 */
export async function deriveCaptureItemId(
  input: CaptureItemIdInput,
): Promise<string> {
  // Explicit, field-labelled scope segment so a space id can never be
  // mistaken for a site id (and the two branches mirror the server's two
  // partial unique indexes). The discriminator is `spaceId === null`,
  // EXACTLY the server's: create-item-client-id.ts keys site rows on
  // isNull(space_id) and space rows on eq(space_id, …), so only an explicit
  // null takes the site branch — keeping the derived id aligned with the
  // scope the server resolves idempotency against.
  const scope =
    input.spaceId !== null
      ? `space:${input.spaceId}`
      : `site:${input.siteId}`
  const name = `${input.captureSessionId}|${scope}|${input.assetTypeId}`
  return uuidV5(name, CAPTURE_ITEM_ID_NAMESPACE)
}

/**
 * Fallback id for the rare case where the deterministic id collides with an
 * admin-REJECTED tombstone (a soft-deleted capture_items row: it keeps its
 * PRIMARY KEY but `rejected_at` is set, so it is excluded from the partial
 * unique indexes and from every read). A re-capture of that slot becomes a NEW
 * live row coexisting with the tombstone, so the admin's rejection is preserved
 * AND the surveyor's count is neither swallowed locally nor PK-collided on the
 * server (audit-1 #1).
 *
 * DETERMINISTIC (not random): derived from the colliding `seed` (the slot's own
 * deterministic id) via a distinct `fallback:` segment. So a RETRY of the same
 * tombstoned slot — a re-dispatch, or an offline-logout stash re-inject whose
 * chunk re-runs after a sibling item threw (requeue-stashed-counts.ts) — mints
 * the SAME id and collides idempotently instead of inserting a DUPLICATE live
 * count (Codex — fallback ids must be stable across stash retries). The
 * `fallback:` prefix guarantees the result never equals the tombstone's own id
 * nor any other slot's deriveCaptureItemId output. Still a greppable, named
 * escape (NOT the create path's normal id source) so the deterministic-id lint
 * gate stays meaningful.
 */
export async function mintFallbackCaptureItemId(seed: string): Promise<string> {
  return uuidV5(`fallback:${seed}`, CAPTURE_ITEM_ID_NAMESPACE)
}

/**
 * Cap on the fallback-id GENERATIONS a caller walks when the deterministic id is a
 * rejected tombstone, its fallback is ALSO a tombstone (the slot was rejected → re-added
 * → rejected again), and so on. Deterministic fallbacks are stable per generation (good:
 * a retry collides idempotently), but that means a re-add can't just mint a fresh random
 * id — it must walk to the next generation to stay re-addable (Codex). The cap guarantees
 * the walk TERMINATES: a slot rejected+re-added this many times is pathological, and at
 * worst the last generation's id quarantines on upload — never a wedge, never a loop.
 */
export const MAX_FALLBACK_ID_GENERATIONS = 32

/**
 * Minimal structural view of Web Crypto's digest so this module typechecks
 * without the DOM lib (shared package) while binding to the real
 * implementation in every target runtime (browser, Node ≥ 18, workers).
 */
interface SubtleDigest {
  digest(algorithm: string, data: Uint8Array): Promise<ArrayBuffer>
}

function getSubtle(): SubtleDigest {
  const subtle = (
    globalThis as { crypto?: { subtle?: SubtleDigest } }
  ).crypto?.subtle
  if (subtle === undefined || typeof subtle.digest !== 'function') {
    throw new Error(
      'uuidV5: Web Crypto subtle.digest is unavailable in this runtime',
    )
  }
  return subtle
}

/**
 * RFC 4122 §4.3 name-based UUID v5 (SHA-1). Returns the canonical
 * 8-4-4-4-12 lowercase string. Async because it uses the Web Crypto
 * SubtleCrypto digest — available in the PWA (secure context), the Encore
 * backend (Node ≥ 18), and the Node (@powersync/node) test/e2e runtime
 * alike, so no SHA-1 dependency or hand-rolled hash is introduced.
 *
 * Exported (beyond deriveCaptureItemId) so it can be pinned to the
 * published RFC golden vectors in its unit test, proving the bit-twiddling
 * is correct independently of the project namespace.
 */
export async function uuidV5(name: string, namespace: string): Promise<string> {
  const subtle = getSubtle()
  const namespaceBytes = parseUuidToBytes(namespace)
  const nameBytes = new TextEncoder().encode(name)
  const input = new Uint8Array(namespaceBytes.length + nameBytes.length)
  input.set(namespaceBytes, 0)
  input.set(nameBytes, namespaceBytes.length)

  const digest = new Uint8Array(await subtle.digest('SHA-1', input))
  const bytes = digest.slice(0, 16)
  // Set the version (5) in the high nibble of octet 6 and the RFC 4122
  // variant (10xx) in the high bits of octet 8. `?? 0` satisfies
  // noUncheckedIndexedAccess; a 16-byte slice always has these octets.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x50
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  return bytesToUuid(bytes)
}

/** Parse a canonical UUID string into its 16 big-endian bytes. */
function parseUuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '')
  if (hex.length !== 32 || /[^0-9a-fA-F]/.test(hex)) {
    throw new Error(`uuidV5: invalid namespace UUID "${uuid}"`)
  }
  const bytes = new Uint8Array(16)
  for (let i = 0; i < 16; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return bytes
}

/** Format 16 bytes as a canonical lowercase 8-4-4-4-12 UUID string. */
function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
  )
}
